<template>
  <section class="pad-t-0 admin-users-page">
    <div class="bb-container">
      <div class="back">
        <div class="back__back">
          <a @click.prevent="$router.back()">
            <div class="back__name">
              <img
                src="../../assets/img/ico/ico-arrow-back.svg"
                loading="lazy"
                alt="Volver"
                class="back__arrow"
              />
            </div>
          </a>
        </div>
      </div>

      <div class="g-edit">
        <div class="g-edit__header">
          <h1 class="g-edit__title">Usuarios</h1>
        </div>

        <Notification
          v-if="successMsg"
          tipo="success"
          @close-notification="successMsg = null"
        >
          {{ successMsg }}
        </Notification>

        <Notification
          v-if="errorMsg"
          tipo="error"
          @close-notification="errorMsg = null"
        >
          {{ errorMsg }}
        </Notification>

        <div class="users-toolbar">
          <div class="bb-form-item users-toolbar__search">
            <input
              v-model="searchQuery"
              class="bb-form"
              type="text"
              placeholder="Buscar por email..."
              @input="onSearchInput"
            />
          </div>
        </div>

        <div v-if="loading" class="mar-t-2">
          <loading-button></loading-button>
        </div>

        <template v-else>
          <div v-if="!hasSearched" class="users-empty">
            <p>Escribe un email para buscar usuarios.</p>
          </div>

          <div v-else-if="users.length === 0" class="users-empty">
            <p>No hay usuarios que coincidan con "{{ lastQuery }}".</p>
          </div>

          <div v-else class="users-table">
            <div class="users-table__head">
              <div class="users-table__col users-table__col--name">Nombre</div>
              <div class="users-table__col users-table__col--email">Email</div>
              <div class="users-table__col users-table__col--date">Fecha de alta</div>
              <div class="users-table__col users-table__col--status">Estado</div>
              <div class="users-table__col users-table__col--actions">Acciones</div>
            </div>

            <div
              v-for="user in users"
              :key="user.id"
              class="users-table__row"
            >
              <div class="users-table__col users-table__col--name">
                <span class="users-table__label">Nombre:</span>
                {{ user.name || "—" }}
              </div>
              <div class="users-table__col users-table__col--email">
                <span class="users-table__label">Email:</span>
                {{ user.email }}
              </div>
              <div class="users-table__col users-table__col--date">
                <span class="users-table__label">Fecha de alta:</span>
                {{ formatDate(user.createdAt) }}
              </div>
              <div class="users-table__col users-table__col--status">
                <span class="users-table__label">Estado:</span>
                <span
                  class="badge"
                  :class="user.isDeleted ? 'badge--muted' : 'badge--ok'"
                >
                  {{ user.isDeleted ? "Eliminado" : "Activo" }}
                </span>
              </div>
              <div class="users-table__col users-table__col--actions">
                <button
                  v-if="!user.isDeleted"
                  type="button"
                  class="button button--danger button--small"
                  @click="confirmDeleteUser(user)"
                >
                  Eliminar cuenta
                </button>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Modal Confirmación Eliminar -->
    <modal-dialog
      :show="showDeleteModal"
      @close="closeDeleteModal"
      target-class="modal__card--small"
    >
      <div class="create-retailer-modal">
        <h3 class="header-3" style="margin-bottom: 16px;">Eliminar cuenta</h3>
        <p>
          ¿Seguro que quieres eliminar la cuenta de
          <strong>{{ userToDelete ? (userToDelete.name || userToDelete.email) : "" }}</strong>?
        </p>
        <p style="font-size: 0.875rem; color: #6c757d; margin-top: 8px;">
          Esta acción anonimiza el email, nombre y foto del usuario y no se puede deshacer.
        </p>

        <div style="display: flex; gap: 12px; margin-top: 24px; justify-content: flex-end;">
          <button
            type="button"
            @click="closeDeleteModal"
            class="button button--secondary"
          >
            Cancelar
          </button>
          <loading-button v-if="deletingUser"></loading-button>
          <button
            v-else
            type="button"
            @click="deleteUser"
            class="button button--danger"
          >
            Eliminar
          </button>
        </div>

        <div v-if="deleteError" class="bb-form-error" style="margin-top: 16px;">
          {{ deleteError }}
        </div>
      </div>
    </modal-dialog>
  </section>
</template>

<script>
import LoadingButton from "../../components/LoadingButton";
import ModalDialog from "../../components/ui/ModalDialog";
import Notification from "../../components/ui/Notification.vue";
import { mapGetters } from "vuex";
import axios from "axios";

const API_BASE = process.env.API;

export default {
  name: "AdminUsersPage",
  components: { LoadingButton, ModalDialog, Notification },
  data() {
    return {
      loading: false,
      hasSearched: false,
      errorMsg: null,
      successMsg: null,
      searchQuery: "",
      lastQuery: "",
      searchDebounce: null,
      users: [],

      // Modal eliminar
      showDeleteModal: false,
      userToDelete: null,
      deletingUser: false,
      deleteError: null,
    };
  },
  computed: {
    ...mapGetters({ auth_token: "auth_token" }),
  },
  methods: {
    formatDate(iso) {
      if (!iso) return "—";
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    },

    onSearchInput() {
      clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => {
        this.searchUsers();
      }, 350);
    },

    async searchUsers() {
      const query = this.searchQuery.trim();
      this.errorMsg = null;

      if (!query) {
        this.users = [];
        this.hasSearched = false;
        this.lastQuery = "";
        return;
      }

      this.loading = true;
      try {
        const res = await axios.get(`${API_BASE}/admin/users`, {
          params: { email: query },
          headers: { Authorization: `Bearer ${this.auth_token}` },
        });

        this.users = res.data || [];
        this.lastQuery = query;
        this.hasSearched = true;
      } catch (e) {
        console.error("Error buscando usuarios:", e);
        this.errorMsg = this.getErrorMessage(e, "No se pudieron buscar usuarios.");
        this.users = [];
      } finally {
        this.loading = false;
      }
    },

    getErrorMessage(e, fallback) {
      if (e.response?.status === 403) {
        return "No tienes permisos de administrador para realizar esta acción.";
      }
      if (e.response?.status === 404) {
        return "El usuario ya no existe.";
      }
      return e.response?.data?.message || fallback;
    },

    // ---------- Eliminar ----------
    confirmDeleteUser(user) {
      this.userToDelete = user;
      this.deleteError = null;
      this.showDeleteModal = true;
    },

    closeDeleteModal() {
      this.showDeleteModal = false;
      this.userToDelete = null;
      this.deleteError = null;
    },

    async deleteUser() {
      if (!this.userToDelete) return;

      this.deleteError = null;
      this.deletingUser = true;

      try {
        await axios.delete(`${API_BASE}/admin/users/${this.userToDelete.id}`, {
          headers: { Authorization: `Bearer ${this.auth_token}` },
        });

        const deletedUser = this.userToDelete;
        const index = this.users.findIndex((u) => u.id === deletedUser.id);
        if (index !== -1) {
          this.$set(this.users, index, {
            ...this.users[index],
            isDeleted: true,
            deletedAt: new Date().toISOString(),
          });
        }

        this.successMsg = `Cuenta de "${deletedUser.name || deletedUser.email}" eliminada correctamente.`;
        this.closeDeleteModal();
      } catch (e) {
        console.error("Error eliminando usuario:", e);
        if (e.response?.status === 404) {
          this.deleteError = "El usuario ya no existe o ya fue eliminado.";
        } else {
          this.deleteError = this.getErrorMessage(e, "No se pudo eliminar la cuenta. Intenta de nuevo.");
        }
      } finally {
        this.deletingUser = false;
      }
    },
  },
  beforeDestroy() {
    clearTimeout(this.searchDebounce);
  },
};
</script>

<style scoped>
.users-toolbar {
  display: flex;
  gap: 1rem;
  align-items: flex-end;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.users-toolbar__search {
  flex: 1;
  min-width: 240px;
  margin-bottom: 0;
}

.users-empty {
  padding: 2rem;
  text-align: center;
  background: #f8f9fa;
  border: 1px dashed #dee2e6;
  border-radius: 4px;
  color: #6c757d;
}

.users-table {
  border: 1px solid #dee2e6;
  border-radius: 4px;
  overflow: hidden;
  background: #fff;
}

.users-table__head {
  display: grid;
  grid-template-columns: 1fr 1fr 140px 110px 160px;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  font-weight: 600;
  font-size: 0.875rem;
  color: #495057;
}

.users-table__row {
  display: grid;
  grid-template-columns: 1fr 1fr 140px 110px 160px;
  gap: 1rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #f1f3f5;
  align-items: center;
}

.users-table__row:last-child {
  border-bottom: none;
}

.users-table__row:hover {
  background: #fafbfc;
}

.users-table__col {
  display: flex;
  align-items: center;
}

.users-table__col--date {
  color: #6c757d;
  font-size: 0.875rem;
}

.users-table__col--actions {
  justify-content: flex-end;
}

.users-table__label {
  display: none;
  font-weight: 600;
  margin-right: 0.5rem;
  color: #495057;
}

.badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 12px;
  line-height: 1;
}

.badge--ok {
  background: rgba(40, 167, 69, 0.12);
}

.badge--muted {
  background: rgba(0, 0, 0, 0.08);
}

.create-retailer-modal {
  padding: 8px;
}

@media (max-width: 768px) {
  .users-table__head {
    display: none;
  }

  .users-table__row {
    grid-template-columns: 1fr;
    gap: 0.5rem;
    padding: 1rem;
  }

  .users-table__col--actions {
    justify-content: flex-start;
    margin-top: 0.5rem;
  }

  .users-table__label {
    display: inline;
  }
}
</style>
